/**
 * Access configurator live preview — photo stack, layered SVG, layer stack, or variant swap.
 */

import { useEffect, useState, type ReactNode } from "react";
import type {
  AccessCategoryId,
  AccessGateVariant,
  AccessPhotoCatalogStored,
  AccessStackLayer,
  BarrierFinish,
  BarrierModel,
  BarrierSupport,
  DockLevellerType,
  DockSealType,
  HighSpeedFrame,
  HighSpeedGlazing,
  SwingFrame,
  SwingGlazing,
  SwingPattern,
} from "@shared/access-configurator";
import {
  accessBarrierLayers,
  accessLoadingDockLayers,
  accessPhotoStackPaths,
  accessPreviewAssetPath,
  getAccessCategory,
  LOADING_DOCK_BLEED_SCALE,
  type AccessPhotoStackPaths,
} from "@shared/access-configurator";

export interface AccessPreviewState {
  categoryId: AccessCategoryId;
  gateVariant: AccessGateVariant;
  colorHex: string;
  /** RAL code (`RAL 9016`) or a swing foil id (`zlaty-dub`). */
  colorCode?: string;
  /** Legacy look key, or a photo combination token sent with the lead. */
  visualVariant: string;
  motif?: string;
  surface?: string;
  cassette?: string;
  swingPattern?: SwingPattern;
  swingFrame?: SwingFrame;
  swingGlazing?: SwingGlazing;
  highSpeedFrame?: HighSpeedFrame;
  highSpeedGlazing?: HighSpeedGlazing;
  barrierModel?: BarrierModel;
  barrierFinish?: BarrierFinish;
  barrierLed?: boolean;
  barrierSkirt?: boolean;
  barrierSupport?: BarrierSupport;
  /** Boom length in mm (barrier) or opening width (unused by other previews). */
  lengthMm?: number;
  dockLeveller?: boolean;
  dockLevellerType?: DockLevellerType;
  dockSeal?: boolean;
  dockSealType?: DockSealType;
  dockBumpers?: boolean;
  dockDoor?: boolean;
}

interface AccessPreviewProps {
  state: AccessPreviewState;
  photoCatalog?: AccessPhotoCatalogStored | null;
  className?: string;
}

function layeredBaseVariant(categoryId: AccessCategoryId, gateVariant: AccessGateVariant): string {
  if (categoryId === "residential_garage") {
    return gateVariant === "tilt" ? "tilt" : "sectional";
  }
  if (categoryId === "swing") {
    return gateVariant === "single_leaf" ? "single_leaf" : "double_leaf";
  }
  return "default";
}

function LegacyPreview({ state, className }: AccessPreviewProps) {
  const mode = getAccessCategory(state.categoryId)?.previewMode;
  if (mode === "variant_swap" || mode === "layer_stack" || state.categoryId === "high_speed" || state.categoryId === "barrier") {
    const src = accessPreviewAssetPath(
      state.categoryId,
      "variant",
      state.categoryId === "high_speed" || state.categoryId === "barrier" ? "a" : state.visualVariant || "a"
    );
    return (
      <div className={`relative w-full aspect-[4/3] flex items-center justify-center ${className ?? ""}`}>
        <img src={src} alt="" className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  const baseVar = layeredBaseVariant(state.categoryId, state.gateVariant);
  const baseSrc = accessPreviewAssetPath(state.categoryId, "base", baseVar);
  const fillSrc = accessPreviewAssetPath(state.categoryId, "fill");

  return (
    <div className={`relative w-full aspect-[4/3] flex items-center justify-center ${className ?? ""}`}>
      <div className="relative w-full h-full max-w-2xl mx-auto">
        <img src={fillSrc} alt="" className="absolute inset-0 w-full h-full object-contain" />
        <div
          className="absolute inset-[12%] rounded-sm opacity-90"
          style={{ backgroundColor: state.colorHex }}
          aria-hidden
        />
        <img src={baseSrc} alt="" className="absolute inset-0 w-full h-full object-contain z-10" />
      </div>
    </div>
  );
}

function PhotoStack({
  layers,
  fallback,
  className,
}: {
  layers: AccessPhotoStackPaths;
  fallback: ReactNode;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [layers.base, layers.overlay, layers.detail]);
  if (failed) return <>{fallback}</>;
  return (
    <div className={`relative w-full aspect-[4/3] flex items-center justify-center ${className ?? ""}`}>
      <div className="relative w-full h-full max-w-2xl mx-auto">
        <img
          src={layers.overlay}
          srcSet={`${layers.overlay} 1x, ${layers.overlay2x} 2x`}
          alt=""
          className="absolute inset-0 w-full h-full object-contain"
        />
        <img
          src={layers.base}
          srcSet={`${layers.base} 1x, ${layers.base2x} 2x`}
          alt=""
          className="absolute inset-0 w-full h-full object-contain z-10"
          onError={() => setFailed(true)}
        />
        {layers.detail ? (
          <img
            src={layers.detail}
            srcSet={`${layers.detail} 1x, ${layers.detail2x ?? layers.detail} 2x`}
            alt=""
            className="absolute inset-0 w-full h-full object-contain z-20 pointer-events-none"
          />
        ) : null}
      </div>
    </div>
  );
}

export default function AccessPreview({ state, photoCatalog, className }: AccessPreviewProps) {
  const def = getAccessCategory(state.categoryId);
  const mode = def?.previewMode ?? "layered";

  if (mode === "layer_stack") {
    const layers =
      state.categoryId === "loading_dock"
        ? accessLoadingDockLayers({
            leveller: state.dockLeveller,
            levellerType: state.dockLevellerType,
            door: state.dockDoor,
            bumpers: state.dockBumpers,
            seal: state.dockSeal,
            sealType: state.dockSealType,
          })
        : accessBarrierLayers({
            model: state.barrierModel,
            finish: state.barrierFinish,
            lengthMm: state.lengthMm,
            led: state.barrierLed,
            skirt: state.barrierSkirt,
            support: state.barrierSupport,
          });
    return <LayerStack layers={layers} state={state} className={className} />;
  }

  if (mode === "variant_swap") {
    const src = accessPreviewAssetPath(state.categoryId, "variant", state.visualVariant || "a");
    return (
      <div className={`relative w-full aspect-[4/3] flex items-center justify-center ${className ?? ""}`}>
        <img src={src} alt="" className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  if (mode === "photo_stack") {
    const layers = accessPhotoStackPaths(
      {
        categoryId: state.categoryId,
        colorCode: state.colorCode,
        motif: state.motif,
        surface: state.surface,
        cassette: state.cassette,
        gateVariant: state.gateVariant,
        swingPattern: state.swingPattern,
        swingFrame: state.swingFrame,
        swingGlazing: state.swingGlazing,
        frame: state.highSpeedFrame,
        glazing: state.highSpeedGlazing,
      },
      photoCatalog
    );
    if (!layers) return null;
    return <PhotoStack layers={layers} className={className} fallback={<LegacyPreview state={state} className={className} />} />;
  }

  return <LegacyPreview state={state} className={className} />;
}

function LayerStack({
  layers,
  state,
  className,
}: AccessPreviewProps & { layers: AccessStackLayer[] }) {
  const [failed, setFailed] = useState(false);
  const [bleedFailed, setBleedFailed] = useState(false);
  const signature = layers.map((layer) => layer.src).join("|");
  useEffect(() => {
    setFailed(false);
    setBleedFailed(false);
  }, [signature]);
  if (failed) {
    if (state.categoryId === "loading_dock") {
      return <div className={`w-full h-full min-h-[12rem] bg-muted ${className ?? ""}`} />;
    }
    return <LegacyPreview state={state} className={className} />;
  }

  const wantsBleed =
    state.categoryId === "loading_dock" && layers[0]?.src.includes("base-bleed");
  const useBleed = wantsBleed && !bleedFailed;

  /** Bleed base: 3× canvas centered on a 4:3 focal box; extras fill the column. */
  if (useBleed) {
    const base = layers[0]!;
    const overlays = layers.slice(1);
    const scalePct = `${LOADING_DOCK_BLEED_SCALE * 100}%`;
    return (
      <div
        className={`relative w-full h-full min-h-0 overflow-hidden bg-white ${className ?? ""}`}
        style={{ containerType: "size" }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Always true 4:3: fit inside the column without stretching */}
          <div
            className="relative overflow-visible"
            style={{
              aspectRatio: "4 / 3",
              width: "min(100cqw, calc(100cqh * 4 / 3))",
              height: "auto",
              maxHeight: "100cqh",
            }}
          >
            <img
              key={`${base.src}-bleed`}
              src={base.src}
              srcSet={`${base.src} 1x, ${base.src2x} 2x`}
              alt=""
              className="absolute left-1/2 top-1/2 max-w-none -translate-x-1/2 -translate-y-1/2"
              style={{
                width: scalePct,
                height: scalePct,
                objectFit: "fill",
                zIndex: 1,
              }}
              onError={() => setBleedFailed(true)}
            />
            {overlays.map((layer, index) => (
              <img
                key={layer.src}
                src={layer.src}
                srcSet={`${layer.src} 1x, ${layer.src2x} 2x`}
                alt=""
                className="absolute inset-0 h-full w-full object-contain"
                style={{ zIndex: index + 2 }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative w-full aspect-[4/3] flex items-center justify-center ${className ?? ""}`}>
      <div className="relative w-full h-full max-w-2xl mx-auto overflow-hidden">
        {layers.map((layer, index) => {
          const src =
            wantsBleed && bleedFailed && index === 0
              ? layer.src.replace("base-bleed", "base")
              : layer.src;
          const src2x =
            wantsBleed && bleedFailed && index === 0
              ? layer.src2x.replace("base-bleed", "base")
              : layer.src2x;
          return (
            <img
              key={src}
              src={src}
              srcSet={`${src} 1x, ${src2x} 2x`}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
              style={{ zIndex: index + 1 }}
              onError={index === 0 ? () => setFailed(true) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}
